import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Chart from 'chart.js/auto';
import ReportComponent from '../utils/ReportComponent/ReportComponent';
import Loading from './Loading';

const reportFormatter = ReportComponent.formatter;
const defLeftScale = {
    type: "linear",
    position: "left",
    display: true,
    ticks: {
        maxTicksLimit: 10,
        stepSize: 0.5
    },
    min: 0
};

const defRightScale = {
    type: "linear",
    position: "right",
    display: true,
    ticks: {
        maxTicksLimit: 10,
        stepSize: 0.5
    },
    grid: {
        drawOnChartArea: false
    },
    min: 0
};

function ChartLine(props) {
    const { classNameWrapp, classNameCanvas, height, yAxesLeft, yAxesRight, loading, chartData, parameters } = props;
    const canvasRef = useRef(null);
    const chartRef = useRef(null);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    beforeTitle: (items) => {
                        const keys = { 0: yAxesLeft, 1: yAxesRight };
                        items.forEach((item) => {
                            const metricKey = keys[item.datasetIndex];
                            if (typeof item.yLabel !== 'undefined') {
                                item.yLabel = reportFormatter.format(item.yLabel, metricKey, parameters);
                            }
                        });
                    }
                }
            }
        },
        scales: {}
    }), [yAxesLeft, yAxesRight, parameters]);

    const buildData = () => {
        const data = { labels: [], datasets: [] };
        if (!chartData || !chartData.labels || !chartData.labels.length) return data;
        data.labels = chartData.labels;
        const left = chartData.metricsData[yAxesLeft];
        const right = yAxesRight ? chartData.metricsData[yAxesRight] : null;
        if (right) {
            data.datasets.push({
                label: right.title,
                data: right.data,
                metric: yAxesRight,
                borderColor: '#000056',
                backgroundColor: 'rgba(220,220,220,0)',
                yAxisID: 'y1'
            });
        }
        if (left) {
            data.datasets.push({
                label: left.title,
                data: left.data,
                metric: yAxesLeft,
                borderColor: '#FD267E',
                backgroundColor: 'rgba(220,220,220,0)',
                yAxisID: 'y'
            });
        }
        return data;
    };

    const applyScales = (opts, data) => {
        opts.scales = opts.scales || {};
        if (data.datasets.some(d => d.yAxisID === 'y')) {
            opts.scales.y = {
                ...defLeftScale,
                ticks: {
                    ...defLeftScale.ticks,
                    callback: (tick) => reportFormatter.format(tick, yAxesLeft, parameters)
                }
            };
        }
        if (data.datasets.some(d => d.yAxisID === 'y1')) {
            opts.scales.y1 = {
                ...defRightScale,
                ticks: {
                    ...defRightScale.ticks,
                    callback: (tick) => reportFormatter.format(tick, yAxesRight, parameters)
                }
            };
        } else {
            delete opts.scales.y1;
        }
    };

    useEffect(() => {
        parameters.isHtml = false;
    }, [parameters]);

    useEffect(() => {
        if (!canvasRef.current) return;
        if (chartRef.current) {
            chartRef.current.destroy();
            chartRef.current = null;
        }
        const data = buildData();
        const opts = JSON.parse(JSON.stringify(options));
        applyScales(opts, data);
        chartRef.current = new Chart(canvasRef.current.getContext('2d'), {
            type: 'line',
            data,
            options: opts
        });
        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [chartData, yAxesLeft, yAxesRight, options]);

    useEffect(() => {
        if (!props.getMetrics || !chartData || !chartData.metricsData) return;
        const list = Object.keys(chartData.metricsData).map(key => ({ value: key, title: chartData.metricsData[key].title }));
        props.getMetrics(list);
    }, [chartData, props]);

    return (
        <div className={`loading-wrapper ${classNameWrapp}`}>
            <canvas
                className={classNameCanvas}
                height={height}
                ref={canvasRef}
            />
            {loading ? <div className="loading-wrapper__item"><Loading/></div> : null}
        </div>
    );
}

ChartLine.propTypes = {
    classNameWrapp: PropTypes.string,
    classNameCanvas: PropTypes.string,
    height: PropTypes.number,
    yAxesLeft: PropTypes.string,
    yAxesRight: PropTypes.string,
    loading: PropTypes.bool,
    chartData: PropTypes.shape({
        labels: PropTypes.array,
        metricsData: PropTypes.object
    })
};

ChartLine.defaultProps = {
    classNameWrapp: '',
    classNameCanvas: '',
    height: 300,
    yAxesLeft: 'PublisherCreditTotalAmountConfirmed',
    yAxesRight: '',
    loading: false,
    chartData: {
        labels: [],
        metricsData: {}
    }
};

export default ChartLine;
